<template>
  <FormQuestion
    :header="questionHeader"
    :subtitle="subsidyProgram.preference_subtitle"
    :title="subsidyProgram.preference_title"
    hide-actions
  >
    <v-card
      class="mb-4"
      border
      flat
      tile
    >
      <v-card-title>
        <h4 class="fs-18">
          {{ $bct('Ranked selections') }}
        </h4>
      </v-card-title>

      <v-divider />

      <v-card-text class="px-0 py-0">
        <div class="bg-super-light-blue px-4 py-3">
          <SelectedProgramCard
            v-for="(programId, index) in programIds"
            @open="openProvider($event)"
            :key="programId"
            :index="index"
            :length="programIds.length"
            :program-id="programId"
            class="my-4"
            active
            ordered
            readonly
          />
        </div>
      </v-card-text>
    </v-card>

    <template v-if="subsidyProgram.enable_current_provider_selection">
      <v-card
        border
        flat
        tile
      >
        <v-card-title>
          <h4 class="fs-18">
            {{ $bct('Current Preschool enrollment') }}
          </h4>
        </v-card-title>

        <v-divider />

        <v-card-text class="px-0 py-0">
          <div class="bg-super-light-blue px-4 py-3">
            <template v-if="currentProviderId">
              <SimpleProviderCard
                :api="api"
                :provider-id="currentProviderId"
                hide-toggle
                hide-view-more
              />
            </template>
            <template v-else>
              <p class="fs-16 mb-0">
                {{ $bct('Not currently enrolled at any of the ranked locations.') }}
              </p>
            </template>
          </div>
        </v-card-text>
      </v-card>

      <template v-if="subsidyProgram.enable_sibling_provider_selection">
        <v-card
          border
          flat
          tile
        >
          <v-card-title>
            <h4 class="fs-18">
              {{ $bct('Sibling(s) currently enrolled at') + ': ' }}
            </h4>
          </v-card-title>

          <v-divider />

          <v-card-text class="px-0 py-0">
            <div class="bg-super-light-blue px-4 py-3">
              <template v-if="siblingProviderIds?.length > 0">
                <SimpleProviderCard
                  v-for="(siblingId, index) in siblingProviderIds"
                  :key="index"
                  :api="api"
                  :provider-id="siblingId"
                  hide-toggle
                  hide-view-more
                />
              </template>
              <template v-else>
                <p class="fs-16 mb-0">
                  {{ $bct('No siblings currently enrolled at any of the ranked locations.') }}
                </p>
              </template>
            </div>
          </v-card-text>
        </v-card>
      </template>

      <template v-if="subsidyProgram.enable_staff_provider_selection">
        <v-card
          border
          flat
          tile
        >
          <v-card-title>
            <h4 class="fs-18">
              {{ $bct('Family member a staff member at:') }}
            </h4>
          </v-card-title>

          <v-divider />

          <v-card-text class="px-0 py-0">
            <div class="bg-super-light-blue px-4 py-3">
              <template v-if="staffProviderIds?.length > 0">
                <SimpleProviderCard
                  v-for="(staffId, index) in staffProviderIds"
                  :key="index"
                  :api="api"
                  :provider-id="staffId"
                  hide-toggle
                  hide-view-more
                />
              </template>
              <template v-else>
                <p class="fs-16 mb-0">
                  {{
                    $bct(
                      'None of the family members are employed as staff at any of the ranked locations.',
                    )
                  }}
                </p>
              </template>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </template>

    <ContentDialog
      ref="providerDialog"
      :max-width="800"
    >
      <template #content="{ data, unmounted }">
        <ProviderMatch
          v-if="data"
          @unmounted="unmounted"
          :match="data"
          :outlined="false"
          :schema="providerSchema"
        />
      </template>
    </ContentDialog>
  </FormQuestion>
</template>

<script setup>
import ContentDialog from '@/shared/components/ContentDialog.vue';
import FormQuestion from '@/shared/components/form/FormQuestion.vue';
import ProviderMatch from '@/shared/components/search/ProviderMatch.vue';
import SelectedProgramCard from '@/parent/components/subsidy/SelectedProgramCard.vue';
import SimpleProviderCard from '@/shared/components/SimpleProviderCard.vue';
import useApi from '@/shared/composables/useApi';
import { useStore } from 'vuex';

const props = defineProps({
  childName: {
    default: null,
    type: String,
  },
  currentProviderId: {
    default: null,
    type: String,
  },
  programIds: {
    default: null,
    type: Array,
  },
  siblingProviderIds: {
    default: null,
    type: Array,
  },
  staffProviderIds: {
    default: null,
    type: Array,
  },
  subsidyProgram: {
    default: null,
    type: Object,
  },
});

const { api } = useApi();
const store = useStore();
const $bct = inject('translator');

const providerDialog = ref(null);

const providerSchema = computed(
  () => store.state.schemas[props.subsidyProgram?.provider_schema_id],
);

const questionHeader = computed(() => {
  return `${$bct('Child')} - ${props.childName}`;
});

const openProvider = async (providerId) => {
  const resp = await api.public_api.provider.get(providerId);

  providerDialog.value.open(resp.data);
};
</script>
